/* @flow */

import * as React from 'react';

import classNames from 'classnames';

import {PopoverNoPortal} from '../popover';

import './range-input.module.css';

type Props = {
    label?: string,
    // Added a string type here because at some point the value becomes a string.
    // We shoud look into this. Possibly a redux-form thing.
    value: number | string,
    onChange: (number) => void,
    customRange?: {
        start: string,
        end: string,
    },
    min?: number,
    max?: number,
    alwaysShowPopover?: boolean,
    popoverValue?: any,
    disabled?: boolean,
    zIndex?: number,
    disableFocusTrapZone?: boolean,
    removePercentageSuffix?: boolean,
    variant?: 'default' | 'small',
};

RangeInput.defaultProps = {
    alwaysShowPopover: true,
    removePercentageSuffix: false,
    variant: 'default',
};

export function RangeInput(props: Props) {
    const [popoverLeftValue, setPopoverLeftValue] = React.useState<string>('0px');
    const rangeRef = React.useRef<?HTMLInputElement>(null);
    const popoverAnchorRef = React.useRef<?HTMLDivElement>(null);

    const calculatePopoverAnchorLeftPosition = React.useCallback(
        (range) => {
            const val = Number(range.value);
            const min = range.min ? Number(range.min) : 0;
            const max = range.max ? Number(range.max) : 100;
            const percent = Number(((val - min) * 100) / (max - min));
            const isSmall = props.variant === 'small';
            const thumbSize = isSmall ? 24 : 36;
            const thumbOffset = isSmall ? 12 : 18;
            const leftValue = `calc(${percent}% + ${thumbOffset}px - ${
                (percent / 100) * thumbSize
            }px)`;

            setPopoverLeftValue(leftValue);
        },
        [props.variant]
    );

    React.useEffect(() => {
        if (rangeRef.current && props.alwaysShowPopover) {
            calculatePopoverAnchorLeftPosition(rangeRef.current);
        }
    }, [rangeRef, props.alwaysShowPopover, props.value, calculatePopoverAnchorLeftPosition]);

    const handleChange = (e: SyntheticInputEvent<*>) => {
        props.onChange(Number(e.target.value));

        // If rangeRef exists, re-calculate the popover position
        if (rangeRef.current) {
            calculatePopoverAnchorLeftPosition(rangeRef.current);
        }
    };

    const isSmall = props.variant === 'small';

    const inputContainerClassName = classNames('input-container', {
        'input-container-small': isSmall,
    });
    const labelClassName = classNames('label', {
        'label-small': isSmall,
    });
    const valueClassName = classNames('value', {
        'value-small': isSmall,
    });
    const customRangeClassName = classNames('custom-range', {
        'custom-range-small': isSmall,
    });
    const sliderClassName = classNames('slider', {
        'slider-small': isSmall,
    });

    return (
        <div>
            {props.label ? <label styleName={labelClassName}>{props.label}</label> : undefined}
            <div styleName={inputContainerClassName}>
                {props.customRange ? undefined : (
                    <div styleName={valueClassName}>
                        {props.removePercentageSuffix ? props.value : `${props.value}%`}
                    </div>
                )}
                {props.popoverValue && popoverAnchorRef.current ? (
                    <PopoverNoPortal
                        anchor={popoverAnchorRef.current}
                        location='top'
                        zIndex={props.zIndex}
                        disableFocusTrapZone={props.disableFocusTrapZone}
                    >
                        <div className='pad-16'>{props.popoverValue}</div>
                    </PopoverNoPortal>
                ) : undefined}
                <input
                    ref={rangeRef}
                    value={props.value}
                    styleName={sliderClassName}
                    type='range'
                    min={props.min}
                    max={props.max}
                    onChange={handleChange}
                    disabled={props.disabled}
                />
                <div ref={popoverAnchorRef} style={{left: popoverLeftValue}} styleName='popover' />
            </div>
            {props.customRange ? (
                <div styleName={customRangeClassName}>
                    <span>{props.customRange.start}</span>
                    <span>{props.customRange.end}</span>
                </div>
            ) : undefined}
        </div>
    );
}
